import React from 'react';
import styled from 'styled-components';
import {Modal, message, Alert} from 'antd';
import qs from 'query-string';
import {getNewOutlet, useOutlet} from 'reconnect.js';
import GenericForm from 'rev.sdk.js/Generic/Form';
import * as JStorage from 'rev.sdk.js/Actions/JStorage';
import * as AppActions from '../AppActions';
import * as AppActionsEx from '../AppActions/custom.js';
import AdminSelectUserCustomWidget from '../Generators/AdminResource/AdminSelectUserCustomWidget';
import {ModalCustomStyle} from '../AppContext/customStyledCss.js';
import Config from '../../data.json';

getNewOutlet('AdminReassignOrderModal', null, {autoDelete: false});

export default function AdminReassignOrderModal(props) {
  const [modalData, setModalData] = useOutlet('AdminReassignOrderModal');
  const [user] = useOutlet('user');

  return (
    <Modal
      title="線下付款"
      footer={null}
      style={{top: 20}}
      bodyStyle={{padding: 0}}
      width={650}
      visible={modalData}
      onOk={() => {
        setModalData(null);
      }}
      onCancel={() => {
        setModalData(null);
      }}
      destroyOnClose={true}>
      <ModalContent
        modalData={modalData}
        setModalData={setModalData}
        user={user}
      />
    </Modal>
  );
}

function AdminSelectUserWithReceiptWidget(props) {
  const onOwnerChange = props?.options?.onOwnerChange;

  return (
    <AdminSelectUserCustomWidget
      {...props}
      onChange={(owner) => {
        props.onChange(owner);
        if (typeof onOwnerChange === 'function') {
          onOwnerChange(owner);
        }
      }}
    />
  );
}

function ModalContent(props) {
  const {modalData, setModalData} = props;
  const {instance} = modalData;
  const [selectedOwner, setSelectedOwner] = React.useState(
    instance?.owner || '',
  );
  const [formInstance, setFormInstance] = React.useState(instance);
  const [loadingReceipt, setLoadingReceipt] = React.useState(false);
  const [receiptPreview, setReceiptPreview] = React.useState({
    name: '',
    receipt_title: '',
    receipt_gui_number: '',
  });

  const isAnnualFeeOrder = React.useMemo(() => {
    const productName = instance?.items?.[0]?.product?.name || '';
    return productName.indexOf('常年會費') > -1;
  }, [instance]);

  React.useEffect(() => {
    setSelectedOwner(instance?.owner || '');
    setFormInstance(instance);
  }, [instance]);

  React.useEffect(() => {
    let cancelled = false;

    const fetchReceipt = async () => {
      if (!isAnnualFeeOrder || !selectedOwner) {
        setReceiptPreview({
          name: '',
          receipt_title: '',
          receipt_gui_number: '',
        });
        return;
      }

      try {
        setLoadingReceipt(true);
        const profile = await JStorage.fetchOneDocument('user_profile', {
          owner: selectedOwner,
        });

        if (cancelled) {
          return;
        }

        setReceiptPreview({
          name: profile?.name || '',
          receipt_title: profile?.receipt_title || '',
          receipt_gui_number: profile?.receipt_gui_number || '',
        });
      } catch (err) {
        if (!cancelled) {
          setReceiptPreview({
            name: '',
            receipt_title: '',
            receipt_gui_number: '',
          });
        }
      } finally {
        if (!cancelled) {
          setLoadingReceipt(false);
        }
      }
    };

    fetchReceipt();

    return () => {
      cancelled = true;
    };
  }, [isAnnualFeeOrder, selectedOwner]);

  const rjsfProps = {
    widgets: {
      'admin-select-user-custom-widget': AdminSelectUserWithReceiptWidget,
    },
  };

  return (
    <Wrapper>
      <Alert
        showIcon
        type="warning"
        message={
          <div>
            <p>請注意：進行本操作前，請先確認已向會員本人收到款項。</p>
            <p>
              請注意：金額可能依據會員狀態、次狀態，以及是否使用進修時數、免費時數而有不同。
            </p>
            <p>請注意：確認完成後，系統會修改付款狀態為「成功」。</p>
          </div>
        }
        style={{marginBottom: 10}}
      />

      {isAnnualFeeOrder && (
        <Alert
          showIcon
          type="warning"
          message={
            <div>
              <p>
                此筆為常年會費訂單，付款成功後將自動帶入會員收據資料（
                {loadingReceipt
                  ? '讀取中...'
                  : receiptPreview.name || '未指定會員'}
                ）。
              </p>
              <p>
                即將帶入收據抬頭：
                {loadingReceipt
                  ? '讀取中...'
                  : receiptPreview.receipt_title || '未設定'}
              </p>
              <p>
                即將帶入收據統編：
                {loadingReceipt
                  ? '讀取中...'
                  : receiptPreview.receipt_gui_number || '未設定'}
              </p>
            </div>
          }
          style={{marginBottom: 10}}
        />
      )}

      <div className="container">
        <GenericForm
          rjsfProps={rjsfProps}
          instance={formInstance}
          schema={{
            title: '',
            type: 'object',
            required: ['owner', 'offline_date', 'total', 'staff_note'],
            properties: {
              owner: {type: 'string', title: '指定會員'},
              payment_offline_method: {
                type: 'string',
                enum: ['', '郵政劃撥', '傳真刷卡', '現金', 'ATM轉帳匯款'],
                title: '線下方式',
              },
              offline_date: {
                type: 'string',
                title: '線下收款日期',
                format: 'date',
              },
              total: {
                type: 'integer',
                title: '總金額',
              },
              staff_note: {
                type: 'string',
                title: '備註',
              },
            },
          }}
          uiSchema={{
            owner: {
              'ui:widget': 'admin-select-user-custom-widget',
              'ui:options': {
                label: false,
                onOwnerChange: (owner) => {
                  const nextOwner = owner || '';
                  setSelectedOwner(nextOwner);
                  setFormInstance((prev) => ({
                    ...(prev || {}),
                    owner: nextOwner,
                  }));
                },
              },
            },
          }}
          onSubmit={async (formData, extValue) => {
            AppActions.setLoading(true);

            try {
              const targetOwner = selectedOwner || formData.owner || '';

              if (targetOwner) {
                const upr = await JStorage.fetchOneDocument('user_profile', {
                  owner: targetOwner,
                });
                const ppr = await JStorage.fetchOneDocument('private_profile', {
                  owner: targetOwner,
                });

                const data = {
                  payment_offline_method: formData.payment_offline_method || '',
                  offline_date: formData.offline_date || '',
                  //buyer
                  buyer_name: upr.name,
                  buyer_phone: upr.phone,
                  buyer_email: upr.email,
                  buyer_zip: upr.mail_address_zip_code || '',
                  buyer_city: upr.mail_address_city?.split('-')[0] || '',
                  buyer_district: upr.mail_address_city?.split('-')[1] || '',
                  buyer_address: upr.mail_address_detail || '',
                  //receiver
                  receiver_name: upr.name,
                  receiver_phone: upr.phone,
                  receiver_email: upr.email,
                  receiver_zip: upr.mail_address_zip_code || '',
                  receiver_city: upr.mail_address_city?.split('-')[0] || '',
                  receiver_district: upr.mail_address_city?.split('-')[1] || '',
                  receiver_address: upr.mail_address_detail || '',
                  owner_type: ppr.user_type,
                  staff_note: instance.staff_note
                    ? `${instance.staff_note}  ${formData.staff_note}`
                    : formData.staff_note,
                };

                if (isAnnualFeeOrder) {
                  const receiptTitle = upr?.receipt_title || '';
                  const receiptGuiNumber = upr?.receipt_gui_number || '';

                  data.extra_data = {
                    ...instance.extra_data,
                    receipt_title: receiptTitle,
                    receipt_gui_number: receiptGuiNumber,
                  };
                }

                if (instance.owner !== targetOwner) {
                  data.owner = targetOwner;
                }

                const order = await AppActionsEx.reassignOrder({
                  order_number: instance.order_number,
                  data,
                });

                setModalData(null);
                message.success('現場付款成功，請稍候......');

                setTimeout(() => {
                  const params = qs.parse(window.location.search) || {};
                  const path = `${Config.webHost}/admin/order/?action=detail&id=${order.id}`;
                  const defaultBackPath = `/admin/orders/export`;

                  if (params.backPath) {
                    window.location.replace(
                      `${path}&backPath=${params.backPath}`,
                    );
                  } else {
                    window.location.replace(
                      `${path}&backPath=${defaultBackPath}`,
                    );
                  }
                }, 300);
              } else {
                message.error('未設定會員');
              }
              AppActions.setLoading(false);
            } catch (err) {
              console.warn(err);
              message.error('現場付款失敗');
              AppActions.setLoading(false);
            }
          }}
        />
      </div>
    </Wrapper>
  );
}

const Wrapper = styled(ModalCustomStyle)`
  padding: 20px;

  & > .container {
    margin-top: 10px;
    margin-bottom: 20px;
  }
`;
